import { AccessoryCard } from "./AccessoryCard";
import { useEffect, useRef } from "react";

import type { ChatMessage } from "../types";
import { Avatar } from "./Avatar";
import { BudgetSlider } from "./BudgetSlider";
import { ChoiceChips } from "./ChoiceChips";
import { MessageBubble } from "./MessageBubble";
import {
  RecommendationCard,
  gamesResearched,
  shouldShowCard,
} from "./RecommendationCard";
import { TypingIndicator } from "./TypingIndicator";
import styles from "./MessageList.module.css";

interface Props {
  messages: ChatMessage[];
  sending: boolean;
  busy: boolean;
  status?: string | null;
  onAction: (text: string) => void;
  onChoice: (label: string) => void;
  onBudget: (valueInr: number) => void;
}

export function MessageList({
  messages,
  sending,
  busy,
  status,
  onAction,
  onChoice,
  onBudget,
}: Props) {
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages, sending]);

  // Hide the empty assistant placeholder while AERO is still thinking — the
  // TypingIndicator stands in for it, so we don't show two avatars.
  const visible = messages.filter(
    (m) => !(m.role === "assistant" && m.streaming && !m.text.trim()),
  );

  const lastId = visible[visible.length - 1]?.id;

  // Every game researched up to and including each message. A build that
  // took three turns of clarification searched one game per turn, so the
  // final turn's own queries name only the last one — the card would say it
  // was sized for Call of Duty: Warzone alone after the customer had asked
  // about three games. Accumulating here, where the whole conversation is in
  // hand, is what lets the card name all of them.
  const gamesByMessage = new Map<string, string[]>();
  {
    const queries: string[] = [];
    for (const m of visible) {
      if (m.contract?.research?.queries_run?.length) {
        queries.push(...m.contract.research.queries_run);
      }
      gamesByMessage.set(
        m.id,
        gamesResearched(queries, m.contract?.use_case?.target ?? null),
      );
    }
  }

  return (
    <div className={styles.list} role="log" aria-live="polite">
      {visible.map((m) =>
        m.role === "user" ? (
          <div key={m.id} className={styles.userRow}>
            <MessageBubble message={m} />
          </div>
        ) : (
          <div key={m.id} className={styles.botRow}>
            <Avatar size={28} />
            <div className={styles.botCol}>
              <MessageBubble message={m} />
              {m.role === "assistant" && !m.streaming && shouldShowCard(m.contract) && (
                <RecommendationCard
                  contract={m.contract!}
                  games={gamesByMessage.get(m.id)}
                  onAction={onAction}
                />
              )}
              {m.role === "assistant" &&
                !m.streaming &&
                m.budgetPrompt &&
                m.id === lastId && (
                  <BudgetSlider
                    prompt={m.budgetPrompt}
                    disabled={busy}
                    onPick={onBudget}
                    language={m.contract?.language}
                  />
                )}
              {/* Its own card, under the shortlist and under the budget
                  slider — never inside a laptop's row. Last in the stack
                  because the budget is the decision that changes the
                  shortlist, and this is the one that comes after it. Shown
                  on the last message only, so old category chips do not
                  linger up the transcript. */}
              {/* Not gated on the last message, unlike the budget slider
                  above: a stale slider is misleading, but the accessories
                  someone looked at two taps ago are just history, and
                  dropping them made the previous card vanish as soon as
                  another category was tapped. The machine card persists the
                  same way. */}
              {m.role === "assistant" &&
                !m.streaming &&
                // Boolean(), not a bare .length: an empty array gives 0,
                // and `0 && <X/>` renders the digit 0 into the page.
                Boolean(
                  m.contract?.accessory_options?.length ||
                    m.contract?.accessory_match?.length,
                ) && (
                  <AccessoryCard
                    options={m.contract?.accessory_options}
                    blurb={m.contract?.accessory_blurb}
                    items={m.contract?.accessory_match}
                    language={m.contract?.language}
                    disabled={busy}
                    onPick={onAction}
                  />
                )}
              {m.role === "assistant" &&
                !m.streaming &&
                m.quickReplies != null &&
                m.quickReplies.length > 0 &&
                m.id === lastId && (
                  <ChoiceChips
                    options={m.quickReplies}
                    disabled={busy}
                    onPick={onChoice}
                  />
                )}
            </div>
          </div>
        ),
      )}
      {sending && <TypingIndicator status={status} />}
      <div ref={endRef} />
    </div>
  );
}
